// should we rely on tree-shaking for lodash/flatMap etc? needs testing
import lodashFlatMap from 'lodash/flatMap'
import lodashUniqBy from 'lodash/uniqBy'
import { createSelector } from 'reselect'
import type { State } from 'types/redux'
import type { Recipient } from 'types/list'
import { getContactsList } from '@disco/state-management/selectors/contacts'
import { getPlaylistWithTracks } from '@disco/state-management/selectors/playlists'
import { getTrack } from '@disco/state-management/selectors/tracks'

/**
 * Gets playlist shares from the entities state.
 * @param state - The Redux state
 * @returns Playlist shares object
 */
const getPlaylistShares = (state: State) => state.entities.playlistShares
/**
 * Gets track shares from the entities state.
 * @param state - The Redux state
 * @returns Track shares object
 */
const getTrackShares = (state: State) => state.entities.trackShares

/**
 * Gets playlist shares with contact information populated for recipients.
 * @param state - The Redux state
 * @returns Playlist shares object with contact details populated
 */
export const getPlaylistSharesWithContacts = createSelector(
  [getPlaylistShares, getContactsList],
  (playlistShares, contacts) => {
    const result = {}
    Object.keys(playlistShares).forEach(key => {
      result[key] = {
        ...playlistShares[key],
        recipients: playlistShares[key].recipients.map(r => {
          const contact = r.contact && contacts.find(c => c.id === r.contact)
          if (!contact) return r
          return { ...r, name: contact.name, email: contact.email, contact }
        }),
      }
    })
    return result
  },
)

/**
 * Gets track shares with contact information populated for recipients.
 * @param state - The Redux state
 * @returns Track shares object with contact details populated
 */
export const getTrackSharesWithContacts: (State, any) => any = createSelector(
  [getTrackShares, getContactsList],
  (trackShares, contacts) => {
    const result = {}
    Object.keys(trackShares).forEach(key => {
      result[key] = {
        ...trackShares[key],
        recipients: trackShares[key].recipients.map(r => {
          const contact = r.contact && contacts.find(c => c.id === r.contact)

          if (!contact) return r
          return {
            ...r,
            name: contact.name,
            email: contact.email,
            contact,
          }
        }),
      }
    })
    return result
  },
)

/**
 * Gets track shares with contact details and recipient information for a specific track.
 * @param state - The Redux state
 * @param props - Object containing track parameters
 * @returns Object with track, shares loaded status, and detailed share items
 */
export const trackSharesWithContacts: (State, any) => any = createSelector(
  [getTrack, getTrackShares, getContactsList],
  (track, trackShares, contacts) => {
    if (!track) return undefined
    const { shares } = track
    const sharesLoaded = !!(shares && shares.items)
    let sharesItems: any[] | null = null

    if (sharesLoaded) {
      sharesItems = shares.items
        .map(shareId => trackShares[shareId])
        .filter(Boolean)

      sharesItems = lodashFlatMap(sharesItems, share =>
        share.recipients.map(recipient => {
          let core
          const contact =
            recipient.contact && contacts.find(c => c.id === recipient.contact)

          if (contact) {
            core = {
              id: contact.id,
              name: contact.name,
              email: contact.email,
              contact: contact,
            }
          } else {
            core = {
              id: `${recipient.type}-${recipient.group_id}`,
              recipient_id: recipient.id,
            }
          }

          return {
            ...recipient,
            ...core,
            share_id: share.id,
            disabled: share.disabled,
            recipient_view_url: recipient.recipient_view_url || share.view_url,
            manual: share.manual,
          }
        }),
      )
      sharesItems = lodashUniqBy(
        sharesItems,
        (recipient: Recipient) => recipient.id,
      )
    }

    return {
      track,
      sharesLoaded,
      sharesItems,
    }
  },
)

/**
 * Gets playlist shares with contact details and recipient information for a specific playlist.
 * @param state - The Redux state
 * @param props - Object containing playlist parameters
 * @returns Object with playlist, shares loaded status, and detailed share items
 */
export const playlistSharesWithContacts = createSelector(
  [getPlaylistWithTracks, getPlaylistShares, getContactsList],
  (playlist, playlistShares, contacts) => {
    if (!playlist) return undefined
    const { shares } = playlist
    const sharesLoaded = !!(shares && shares.items)
    let sharesItems: any[] | null = null

    if (sharesLoaded) {
      sharesItems = shares.items
        .map(shareId => playlistShares[shareId])
        .filter(Boolean)
      sharesItems = lodashFlatMap(sharesItems, share =>
        share.recipients.map(recipient => {
          let core
          const contact =
            recipient.contact && contacts.find(c => c.id === recipient.contact)

          if (contact) {
            core = {
              id: contact.id,
              name: contact.name,
              email: contact.email,
              contact: contact,
            }
          } else {
            core = {
              // users, contacts, and businesses might have same id - but it must be unique
              id: `${recipient.type}-${recipient.group_id}`,
              recipient_id: recipient.id,
            }
          }

          return {
            ...recipient,
            ...core,
            share_id: share.id,
            // this is needed ONLY for disabling
            disabled: share.disabled,
            recipient_view_url: recipient.recipient_view_url || share.view_url,
            manual: share.manual,
          }
        }),
      )
      sharesItems = lodashUniqBy<Recipient>(
        sharesItems,
        recipient => recipient.id,
      )
    }

    return {
      playlist,
      sharesLoaded,
      sharesItems,
    }
  },
)
